import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import GoogleAnalytics from "@/components/analytics/GoogleAnalytics";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Hide preloader, static hero, and SEO fallback after React mounts
const hideLoadingElements = () => {
  const preloader = document.getElementById('app-preloader');
  const fallback = document.getElementById('seo-fallback');
  const staticHero = document.getElementById('static-hero');
  
  if (preloader) {
    preloader.classList.add('hidden');
    setTimeout(() => preloader.remove(), 300);
  }
  
  // Hide static hero and SEO fallback - CSS :has() handles this automatically in modern browsers
  if (staticHero) {
    staticHero.classList.add('js-hidden');
  }
  if (fallback) {
    fallback.classList.add('js-hidden');
  }
};

// Lazy load other pages
const SluzbyPage = lazy(() => import("./pages/SluzbyPage"));
const SluzbaDetailPage = lazy(() => import("./pages/SluzbaDetailPage"));
const ONasPage = lazy(() => import("./pages/ONasPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const KontaktPage = lazy(() => import("./pages/KontaktPage"));
const PohotovostPage = lazy(() => import("./pages/PohotovostPage"));
const SpolupracePage = lazy(() => import("./pages/SpolupracePage"));
const CityLandingPage = lazy(() => import("./pages/CityLandingPage"));

// Admin pages
const AdminLoginPage = lazy(() => import("./pages/admin/AdminLoginPage"));
const AdminSetupPage = lazy(() => import("./pages/admin/AdminSetupPage"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminPoptavkyPage = lazy(() => import("./pages/admin/AdminPoptavkyPage"));
const AdminRealizacePage = lazy(() => import("./pages/admin/AdminRealizacePage"));
const AdminSluzbyPage = lazy(() => import("./pages/admin/AdminSluzbyPage"));
const AdminKontaktPage = lazy(() => import("./pages/admin/AdminKontaktPage"));
const AdminCenoveNabidkyPage = lazy(() => import("./pages/admin/AdminCenoveNabidkyPage"));
const AdminFirmaPage = lazy(() => import("./pages/admin/AdminFirmaPage"));
const AdminSpolupracePage = lazy(() => import("./pages/admin/AdminSpolupracePage"));

const queryClient = new QueryClient();

// Page loader
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse bg-muted rounded-lg h-8 w-32" />
  </div>
);

const App = () => {
  // Hide preloader and SEO fallback after React app mounts successfully
  useEffect(() => {
    hideLoadingElements();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <GoogleAnalytics />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/sluzby" element={<SluzbyPage />} />
                <Route path="/sluzby/:slug" element={<SluzbaDetailPage />} />
                <Route path="/o-nas" element={<ONasPage />} />
                <Route path="/realizace" element={<PortfolioPage />} />
                <Route path="/kontakt" element={<KontaktPage />} />
                <Route path="/pohotovost" element={<PohotovostPage />} />
                <Route path="/spoluprace" element={<SpolupracePage />} />
                
                {/* City Landing Pages - hidden from main nav, SEO optimized */}
                <Route path="/elektrikar-praha" element={<CityLandingPage />} />
                <Route path="/elektrikar-usti-nad-labem" element={<CityLandingPage />} />
                <Route path="/elektrikar-most" element={<CityLandingPage />} />
                <Route path="/elektrikar-decin" element={<CityLandingPage />} />
                <Route path="/elektrikar-chomutov" element={<CityLandingPage />} />
                <Route path="/elektrikar-bilina" element={<CityLandingPage />} />
                <Route path="/elektrikar-litomerice" element={<CityLandingPage />} />
                
                {/* Admin routes */}
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/setup" element={<AdminSetupPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/poptavky" element={<AdminPoptavkyPage />} />
                <Route path="/admin/spoluprace" element={<AdminSpolupracePage />} />
                <Route path="/admin/nabidky" element={<AdminCenoveNabidkyPage />} />
                <Route path="/admin/realizace" element={<AdminRealizacePage />} />
                <Route path="/admin/sluzby" element={<AdminSluzbyPage />} />
                <Route path="/admin/kontakt" element={<AdminKontaktPage />} />
                <Route path="/admin/firma" element={<AdminFirmaPage />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
